"use client";

import React from "react";
import { useTranslation } from "react-i18next";
import { DndContext, type DragEndEvent } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS, type Transform } from "@dnd-kit/utilities";
import { ChevronLeft, ChevronRight, GripVertical, Pin, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";

export interface SidebarNavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  contentKey?: string;
  href?: string;
  isPinned?: boolean;
  isFixed?: boolean;
  badgeText?: string;
}

export type SidebarSectionKey =
  | "dashboard"
  | "qr-codes"
  | "orders"
  | "kitchen"
  | "expo"
  | "menu"
  | "retail"
  | "integration-store"
  | "integrations"
  | "customers"
  | "reports"
  | "payments"
  | "training"
  | "staff"
  | "settings"
  | "tasks"
  | "projects"
  | "calendar"
  | "teams"
  | "analytics"
  | "files";

const sidebarEasing = "cubic-bezier(0.33, 1, 0.68, 1)";
const SIDEBAR_TRANSITION_MS = 260;
const sidebarContainerTransition: React.CSSProperties = {
  transitionProperty: "width, padding, background-color, border-color, box-shadow",
  transitionDuration: `${SIDEBAR_TRANSITION_MS}ms`,
  transitionTimingFunction: sidebarEasing,
};
const interactiveTransitionStyle: React.CSSProperties = {
  transitionDuration: `${Math.round(SIDEBAR_TRANSITION_MS * 0.75)}ms`,
  transitionTimingFunction: sidebarEasing,
};

const noop = (): void => undefined;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const isTransformValue = (value: unknown): value is Transform => {
  if (!isRecord(value)) {
    return false;
  }
  const x = value.x;
  const y = value.y;
  return typeof x === "number" && typeof y === "number";
};

const toRecord = (value: unknown): Record<string, unknown> | undefined =>
  isRecord(value) ? value : undefined;

const toHTMLElementCallback = (
  value: unknown,
): ((element: HTMLElement | null) => void) | undefined =>
  typeof value === "function" ? (value as (element: HTMLElement | null) => void) : undefined;

const getInitials = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) {
    return "U";
  }
  const segments = trimmed.split(/\s+/).filter(Boolean);
  if (segments.length === 0) {
    return "U";
  }
  if (segments.length === 1) {
    return segments[0].slice(0, 2).toUpperCase();
  }
  const first = segments[0]?.[0] ?? "";
  const last = segments[segments.length - 1]?.[0] ?? "";
  const initials = `${first}${last}`.trim();
  return initials ? initials.toUpperCase() : "U";
};

function BrandBadge(): React.ReactElement {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-neutral-200/70 bg-white/80 px-3 py-2 shadow-sm dark:border-neutral-800/80 dark:bg-neutral-900/70">
      <div className="flex size-10 items-center justify-center rounded-lg border border-emerald-500/40 bg-emerald-500/10 text-sm font-semibold uppercase tracking-wide text-emerald-600 dark:text-emerald-400">
        QR
      </div>
      <div className="flex flex-col">
        <span className="font-semibold text-neutral-900 dark:text-neutral-50">Braise</span>
        <span className="text-xs text-neutral-500 dark:text-neutral-400">Operator console</span>
      </div>
    </div>
  );
}

function AvatarCircle({ label = "U" }: { label?: string }): React.ReactElement {
  const initials = React.useMemo(() => getInitials(label), [label]);
  return (
    <div className="flex size-8 items-center justify-center rounded-full border border-neutral-200 bg-neutral-100 text-xs text-neutral-600 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-200">
      {initials}
    </div>
  );
}

interface SearchInputProps {
  value: string;
  onChange: (value: string) => void;
}

function SearchInput({ value, onChange }: SearchInputProps): React.ReactElement {
  return (
    <div
      className="flex items-center gap-2 rounded-lg border border-neutral-200/80 bg-neutral-100/80 px-3 py-2 shadow-sm dark:border-neutral-800/70 dark:bg-neutral-900/80"
      style={interactiveTransitionStyle}
    >
      <Search className="h-4 w-4 text-neutral-500 dark:text-neutral-400" />
      <input
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
        }}
        placeholder="Search navigation"
        className="flex-1 bg-transparent text-sm text-neutral-900 outline-none placeholder:text-neutral-400 dark:text-neutral-100 dark:placeholder:text-neutral-500"
      />
    </div>
  );
}

interface NavigationListProps {
  items: SidebarNavItem[];
  activeId: string;
  filter: string;
  onNavigate: (id: string) => void;
  onTogglePin: (id: string) => void;
  onReorder: (orderedIds: string[]) => void;
}

function NavigationList({
  items,
  activeId,
  filter,
  onNavigate,
  onTogglePin,
  onReorder,
}: NavigationListProps): React.ReactElement {
  const { t } = useTranslation(["common"]);
  const normalizedFilter = filter.trim().toLowerCase();
  const isFiltering = normalizedFilter.length > 0;

  const filtered = React.useMemo(() => {
    if (!isFiltering) {
      return items;
    }
    return items.filter((item) => item.label.toLowerCase().includes(normalizedFilter));
  }, [items, isFiltering, normalizedFilter]);

  const pinnedItems = React.useMemo(() => filtered.filter((item) => item.isPinned), [filtered]);
  const unpinnedItems = React.useMemo(() => filtered.filter((item) => !item.isPinned), [filtered]);

  const reorderableItems = React.useMemo(() => {
    if (isFiltering) {
      return [];
    }
    return unpinnedItems.filter((item) => !item.isFixed);
  }, [isFiltering, unpinnedItems]);

  const sortableIds = React.useMemo(() => reorderableItems.map((item) => item.id), [reorderableItems]);

  const handleDragEnd = React.useCallback(
    (event: DragEndEvent) => {
      if (isFiltering || reorderableItems.length === 0) {
        return;
      }
      const { active, over } = event;
      if (!over || active.id === over.id) {
        return;
      }
      const ids = reorderableItems.map((item) => item.id);
      const activeIndex = ids.indexOf(String(active.id));
      const overIndex = ids.indexOf(String(over.id));
      if (activeIndex === -1 || overIndex === -1) {
        return;
      }
      onReorder(arrayMove(ids, activeIndex, overIndex));
    },
    [isFiltering, onReorder, reorderableItems],
  );

  const pinnedLabel = t("sidebar.pinnedLabel", { defaultValue: "Pinned" });
  const pinActionLabel = t("sidebar.actions.pin", { defaultValue: "Pin to sidebar" });
  const unpinActionLabel = t("sidebar.actions.unpin", { defaultValue: "Unpin from sidebar" });
  const dragHandleLabel = t("sidebar.actions.dragHandle", { defaultValue: "Drag to reorder" });
  const noResultsText = t("sidebar.noResults", {
    defaultValue: filter.trim()
      ? `No pages match "${filter.trim()}"`
      : "No pages found",
    query: filter.trim(),
  });

  const showPinnedSection = !isFiltering && pinnedItems.length > 0;
  const listItems = showPinnedSection ? unpinnedItems : filtered;
  const [firstUnpinned, ...remainingUnpinned] = listItems;

  const renderNavItem = React.useCallback(
    (item: SidebarNavItem) => {
      const isActive = item.id === activeId;
      const canDrag = !isFiltering && !item.isPinned && !item.isFixed;
      const itemProps = {
        item,
        isActive,
        onNavigate,
        onTogglePin,
        pinActionLabel,
        unpinActionLabel,
      };
      if (canDrag) {
        return (
          <SortableNavigationItem
            key={item.id}
            {...itemProps}
            dragHandleLabel={dragHandleLabel}
          />
        );
      }
      return <StaticNavigationItem key={item.id} {...itemProps} />;
    },
    [activeId, dragHandleLabel, isFiltering, onNavigate, onTogglePin, pinActionLabel, unpinActionLabel],
  );

  return (
    <DndContext onDragEnd={handleDragEnd} modifiers={[restrictToVerticalAxis]}>
      <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
        <nav className="flex flex-col gap-2">
          {firstUnpinned ? renderNavItem(firstUnpinned) : null}

          {showPinnedSection ? (
            <div className="flex flex-col gap-1">
              <span className="px-3 text-[11px] font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
                {pinnedLabel}
              </span>
              <div className="flex flex-col gap-1">
                {pinnedItems.map((item) => (
                  <StaticNavigationItem
                    key={item.id}
                    item={item}
                    isActive={item.id === activeId}
                    onNavigate={onNavigate}
                    onTogglePin={onTogglePin}
                    pinActionLabel={pinActionLabel}
                    unpinActionLabel={unpinActionLabel}
                  />
                ))}
              </div>
            </div>
          ) : null}

          <div className="flex flex-col gap-1">{remainingUnpinned.map((item) => renderNavItem(item))}</div>

          {filtered.length === 0 ? (
            <div className="rounded-lg border border-dashed border-neutral-200/70 px-3 py-6 text-center text-xs text-neutral-500 dark:border-neutral-800/70 dark:text-neutral-500">
              {noResultsText}
            </div>
          ) : null}
        </nav>
      </SortableContext>
    </DndContext>
  );
}

interface NavigationItemRowProps {
  item: SidebarNavItem;
  isActive: boolean;
  onNavigate: (id: string) => void;
  onTogglePin: (id: string) => void;
  pinActionLabel: string;
  unpinActionLabel: string;
  dragHandle?: React.ReactNode;
  containerStyle?: React.CSSProperties;
  containerRef?: (element: HTMLElement | null) => void;
  isDragging?: boolean;
}

function NavigationItemRow({
  item,
  isActive,
  onNavigate,
  onTogglePin,
  pinActionLabel,
  unpinActionLabel,
  dragHandle,
  containerStyle,
  containerRef,
  isDragging,
}: NavigationItemRowProps): React.ReactElement {
  const isPinned = Boolean(item.isPinned);
  const pinLabel = isPinned ? unpinActionLabel : pinActionLabel;

  const handleNavigate = React.useCallback(() => {
    onNavigate(item.id);
  }, [item.id, onNavigate]);

  const handlePinActivate = React.useCallback(
    (event: React.MouseEvent<HTMLSpanElement> | React.KeyboardEvent<HTMLSpanElement>) => {
      event.stopPropagation();
      event.preventDefault();
      onTogglePin(item.id);
    },
    [item.id, onTogglePin],
  );

  const handlePinKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLSpanElement>) => {
      if (event.key === "Enter" || event.key === " ") {
        handlePinActivate(event);
      }
    },
    [handlePinActivate],
  );

  const renderedHandle = React.useMemo(() => {
    if (!dragHandle || !React.isValidElement<{ className?: string }>(dragHandle)) {
      return null;
    }
    const handleElement = dragHandle as React.ReactElement<{ className?: string }>;
    return React.cloneElement(handleElement, {
      className: cn(
        "pointer-events-auto absolute right-0 top-1/2 -translate-y-1/2 translate-x-full opacity-0 transition-all duration-300 ease-out group-hover/navigation:translate-x-0 group-hover/navigation:opacity-100 group-focus-visible/navigation:translate-x-0 group-focus-visible/navigation:opacity-100",
        handleElement.props.className,
      ),
    });
  }, [dragHandle]);

  const labelShiftClasses = dragHandle
    ? "group-hover/navigation:-translate-x-2 group-hover/navigation:pr-8 group-focus-visible/navigation:-translate-x-2 group-focus-visible/navigation:pr-8"
    : "";

  return (
    <div ref={containerRef ?? undefined} style={containerStyle} className={cn("group/navigation relative", isDragging ? "z-10" : null)}>
      <button
        type="button"
        onClick={handleNavigate}
        className={cn(
          "flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors",
          isActive
            ? "bg-emerald-500/15 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-200"
            : "text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900 dark:text-neutral-300 dark:hover:bg-neutral-800 dark:hover:text-neutral-100",
          isDragging ? "shadow-lg ring-1 ring-emerald-500/40" : null,
        )}
      >
        <div className="flex flex-1 items-center gap-3 overflow-hidden">
          <span className="flex h-8 w-8 items-center justify-center rounded-md border border-neutral-200 bg-neutral-100 text-neutral-500 dark:border-neutral-800/70 dark:bg-neutral-900/80 dark:text-neutral-200">
            {item.icon}
          </span>
          <div className="relative flex-1 overflow-hidden">
            <span
              className={cn(
                "block truncate text-left font-medium transition-all duration-300 ease-out",
                labelShiftClasses,
              )}
            >
              {item.label}
            </span>
            {renderedHandle}
          </div>
          {item.badgeText ? (
            <div className="ml-2 shrink-0">
              <Badge
                variant="outline"
                className={cn(
                  "h-5 select-none border-neutral-200/70 bg-neutral-100/60 px-1.5 py-0 text-[10px] font-semibold tracking-wide text-neutral-600 dark:border-neutral-700/60 dark:bg-neutral-800/60 dark:text-neutral-300",
                  isActive ? "border-emerald-300/60 text-emerald-700 dark:text-emerald-300" : null,
                )}
              >
                {item.badgeText}
              </Badge>
            </div>
          ) : null}
        </div>
        {!item.isFixed ? (
          <span
            role="button"
            tabIndex={0}
            aria-pressed={isPinned}
            onClick={handlePinActivate}
            onKeyDown={handlePinKeyDown}
            className={cn(
              "inline-flex size-8 items-center justify-center rounded-md text-neutral-400 transition-colors hover:text-neutral-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/60 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:text-neutral-500 dark:hover:text-neutral-200 dark:focus-visible:ring-emerald-400/60 dark:focus-visible:ring-offset-neutral-900",
              isPinned ? "text-emerald-600 dark:text-emerald-300" : null,
            )}
            title={pinLabel}
            aria-label={pinLabel}
          >
            <Pin className={cn("h-4 w-4", isPinned ? "fill-current" : null)} />
          </span>
        ) : null}
      </button>
    </div>
  );
}

type NavigationItemBaseProps = Omit<NavigationItemRowProps, "dragHandle" | "containerStyle" | "containerRef" | "isDragging">;

function SortableNavigationItem({
  dragHandleLabel,
  ...props
}: NavigationItemBaseProps & { dragHandleLabel: string }): React.ReactElement {
  const sortable = useSortable({ id: props.item.id });
  const transformValue = isTransformValue(sortable.transform) ? sortable.transform : null;
  const transitionValue = typeof sortable.transition === "string" ? sortable.transition : undefined;
  const style: React.CSSProperties | undefined =
    transformValue || transitionValue
      ? {
          transform: transformValue ? CSS.Transform.toString(transformValue) : undefined,
          transition: transitionValue,
        }
      : undefined;
  const nodeRef = toHTMLElementCallback(sortable.setNodeRef);
  const activatorRef = toHTMLElementCallback(sortable.setActivatorNodeRef);
  const handleAttributes = toRecord(sortable.attributes) ?? {};
  const handleListeners = toRecord(sortable.listeners) ?? {};
  const isDragging = typeof sortable.isDragging === "boolean" ? sortable.isDragging : Boolean(sortable.isDragging);
  const dragHandle = (
    <span
      ref={activatorRef ?? undefined}
      {...handleAttributes}
      {...handleListeners}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
      }}
      tabIndex={handleAttributes?.tabIndex ?? 0}
      role={handleAttributes?.role ?? "button"}
      aria-label={dragHandleLabel}
      title={dragHandleLabel}
      className="inline-flex size-8 items-center justify-center rounded-md text-neutral-400 transition-colors hover:text-neutral-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/60 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:text-neutral-500 dark:hover:text-neutral-200 dark:focus-visible:ring-emerald-400/60 dark:focus-visible:ring-offset-neutral-900"
    >
      <GripVertical className="h-4 w-4" />
    </span>
  );

  return (
    <NavigationItemRow
      {...props}
      dragHandle={dragHandle}
      containerStyle={style}
      containerRef={nodeRef}
      isDragging={isDragging}
    />
  );
}

function StaticNavigationItem(props: NavigationItemBaseProps): React.ReactElement {
  return <NavigationItemRow {...props} />;
}

interface DetailSidebarProps {
  navItems: SidebarNavItem[];
  activeNavId: string;
  onNavigate: (id: string) => void;
  collapsed: boolean;
  onCollapseChange: (collapsed: boolean) => void;
  accountLabel?: string;
  accountSubtitle?: string | null;
  onTogglePin: (id: string) => void;
  onReorder: (orderedIds: string[]) => void;
}

function DetailSidebar({
  navItems,
  activeNavId,
  onNavigate,
  collapsed,
  onCollapseChange,
  accountLabel,
  accountSubtitle,
  onTogglePin,
  onReorder,
}: DetailSidebarProps): React.ReactElement {
  const [searchValue, setSearchValue] = React.useState("");
  const { t } = useTranslation(["common"]);

  React.useEffect(() => {
    setSearchValue("");
  }, [activeNavId]);

  const displayAccountLabel = React.useMemo(() => {
    const trimmed = accountLabel?.trim();
    if (!trimmed) {
      return "Workspace";
    }
    return trimmed;
  }, [accountLabel]);

  const displayAccountSubtitle = React.useMemo(() => {
    if (accountSubtitle === null) {
      return null;
    }
    const trimmed = accountSubtitle?.trim();
    if (!trimmed) {
      return "Operations workspace";
    }
    return trimmed;
  }, [accountSubtitle]);

  if (collapsed) {
    return (
      <aside
        className="flex h-full min-h-0 w-24 flex-col items-center overflow-hidden border-l border-neutral-200/80 bg-white/95 px-3 py-6 transition-all duration-300 dark:border-neutral-800/70 dark:bg-neutral-950/95"
        style={sidebarContainerTransition}
      >
        <div className="flex flex-shrink-0 flex-col items-center gap-4">
          <button
            type="button"
            onClick={() => {
              onCollapseChange(false);
            }}
            className="flex size-10 items-center justify-center rounded-lg border border-neutral-200 bg-neutral-100 text-neutral-600 transition-colors hover:bg-neutral-200 dark:border-neutral-800/70 dark:bg-neutral-900/80 dark:text-neutral-300 dark:hover:bg-neutral-800"
            aria-label="Expand sidebar"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
        <nav className="sidebar-scroll flex flex-1 flex-col items-center gap-3 overflow-y-auto overscroll-contain py-4" aria-label="Collapsed navigation">
          {navItems.map((item) => {
            const isActive = item.id === activeNavId;

            return (
              <button
                key={item.id}
                type="button"
                onClick={() => {
                  onNavigate(item.id);
                }}
                className={cn(
                  "flex size-10 items-center justify-center rounded-lg border border-neutral-200 bg-neutral-100 text-neutral-500 transition-colors hover:bg-neutral-200 dark:border-neutral-800/70 dark:bg-neutral-900/70 dark:text-neutral-300 dark:hover:bg-neutral-800",
                  isActive ? "border-emerald-500/60 bg-emerald-500/15 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-300" : null,
                )}
                aria-label={item.label}
                title={item.label}
              >
                {item.icon}
              </button>
            );
          })}
        </nav>
        <div className="flex flex-shrink-0 flex-col items-center gap-3">
          <AvatarCircle label={displayAccountLabel} />
        </div>
      </aside>
    );
  }

  return (
    <aside
      className="flex h-full min-h-0 w-full flex-col gap-5 overflow-hidden border-l border-neutral-200/80 bg-white p-4 pb-6 transition-all duration-300 dark:border-neutral-800/70 dark:bg-neutral-950/95"
      style={sidebarContainerTransition}
    >
      <div className="flex items-start justify-between gap-3">
        <BrandBadge />
        <button
          type="button"
          onClick={() => {
            onCollapseChange(true);
          }}
          className="flex size-10 items-center justify-center rounded-lg border border-neutral-200 bg-neutral-100 text-neutral-600 transition-colors hover:bg-neutral-200 dark:border-neutral-800/70 dark:bg-neutral-900/80 dark:text-neutral-300 dark:hover:bg-neutral-800"
          aria-label="Collapse sidebar"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
      </div>

      <SearchInput value={searchValue} onChange={setSearchValue} />

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className="sidebar-scroll flex flex-col gap-3 overflow-y-auto overscroll-contain pr-1">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
              {t("sidebar.navigationLabel", { defaultValue: "Navigation" })}
            </span>
            <span className="text-xs text-neutral-500 dark:text-neutral-500">
              {t("sidebar.pageCount", { count: navItems.length, defaultValue: "{{count}} pages" })}
            </span>
          </div>
          <NavigationList
            items={navItems}
            activeId={activeNavId}
            filter={searchValue}
            onNavigate={onNavigate}
            onTogglePin={onTogglePin}
            onReorder={onReorder}
          />
        </div>
      </div>

      <div className="mt-4 flex flex-shrink-0 items-center gap-3 rounded-lg border border-neutral-200/80 bg-neutral-100 px-3 py-3 shadow-sm dark:border-neutral-800/70 dark:bg-neutral-900/70">
        <AvatarCircle label={displayAccountLabel} />
        <div className="flex min-w-0 flex-col">
          <span className="truncate text-sm font-semibold text-neutral-900 dark:text-neutral-100">{displayAccountLabel}</span>
          {displayAccountSubtitle ? (
            <span className="text-xs text-neutral-500 dark:text-neutral-400">{displayAccountSubtitle}</span>
          ) : null}
        </div>
      </div>
    </aside>
  );
}

interface TwoLevelSidebarProps {
  items?: SidebarNavItem[];
  activeSection?: string;
  onSectionChange?: (item: SidebarNavItem) => void;
  onDetailCollapseChange?: (collapsed: boolean) => void;
  accountLabel?: string;
  accountSubtitle?: string | null;
  onTogglePin?: (id: string) => void;
  onReorder?: (orderedIds: string[]) => void;
}

export function TwoLevelSidebar({
  items = DEFAULT_NAV_ITEMS,
  activeSection: controlledActiveSection,
  onSectionChange,
  onDetailCollapseChange,
  accountLabel,
  accountSubtitle,
  onTogglePin,
  onReorder,
}: TwoLevelSidebarProps): React.ReactElement {
  const fallbackId = items[0]?.id ?? DEFAULT_NAV_ITEMS[0]?.id ?? "dashboard";
  const [internalActiveSection, setInternalActiveSection] = React.useState(controlledActiveSection ?? fallbackId);
  const [detailCollapsed, setDetailCollapsed] = React.useState(false);

  React.useEffect(() => {
    if (controlledActiveSection) {
      setInternalActiveSection(controlledActiveSection);
    }
  }, [controlledActiveSection]);

  React.useEffect(() => {
    if (!items.some((item) => item.id === internalActiveSection)) {
      const nextId = items[0]?.id ?? fallbackId;
      setInternalActiveSection(nextId);
    }
  }, [items, internalActiveSection, fallbackId]);

  React.useEffect(() => {
    onDetailCollapseChange?.(detailCollapsed);
  }, [detailCollapsed, onDetailCollapseChange]);

  const activeId = controlledActiveSection ?? internalActiveSection;
  const navItems = items.length > 0 ? items : DEFAULT_NAV_ITEMS;

  const handleSectionChange = (sectionId: string) => {
    if (!controlledActiveSection) {
      setInternalActiveSection(sectionId);
    }
    const selectedItem = navItems.find((item) => item.id === sectionId) ?? navItems[0];
    onSectionChange?.(selectedItem);
  };

  const normalizedAccountLabel = accountLabel?.trim();
  const normalizedAccountSubtitle = accountSubtitle === null ? null : accountSubtitle?.trim();

  return (
    <div className="flex h-full min-h-screen overflow-hidden bg-white text-neutral-900 dark:bg-neutral-950 dark:text-neutral-50 lg:h-full lg:min-h-screen lg:overflow-visible">
      <DetailSidebar
        navItems={navItems}
        activeNavId={activeId}
        onNavigate={handleSectionChange}
        collapsed={detailCollapsed}
        onCollapseChange={setDetailCollapsed}
        accountLabel={normalizedAccountLabel}
        accountSubtitle={normalizedAccountSubtitle}
        onTogglePin={onTogglePin ?? noop}
        onReorder={onReorder ?? noop}
      />
    </div>
  );
}

const DEFAULT_NAV_ITEMS: SidebarNavItem[] = [
  { id: "dashboard", label: "Dashboard", icon: <ChevronRight className="h-4 w-4" />, contentKey: "dashboard" },
  { id: "orders", label: "Orders", icon: <ChevronRight className="h-4 w-4" />, contentKey: "orders" },
  { id: "kitchen", label: "Kitchen", icon: <ChevronRight className="h-4 w-4" />, contentKey: "kitchen" },
  { id: "payments", label: "Payments", icon: <ChevronRight className="h-4 w-4" />, contentKey: "payments" },
  { id: "settings", label: "Settings", icon: <ChevronRight className="h-4 w-4" />, contentKey: "settings" },
];

export default TwoLevelSidebar;
